/**
 * OTRIX Play (admin) — límites y validación LOCAL de los videos de la librería.
 *
 * La validación se hace ANTES de subir porque el video grabado con el teléfono pesa mucho:
 * enterarse del rechazo después de esperar la subida completa es la peor experiencia posible.
 * El backend vuelve a validar (tamaño y mime real); esto solo evita el viaje inútil.
 */

/** Duración máxima de un video de práctica: 5 minutos. */
export const MAX_VIDEO_SECONDS = 300;

/** Tamaño máximo del archivo (debe ir de la mano con MAX_MEDIA_BYTES del backend). */
export const MAX_VIDEO_MB = 200;

const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

/**
 * Lee la duración de los metadatos sin decodificar el video entero (`preload='metadata'`).
 * Devuelve 0 si el navegador no pudo leerlos (códec exótico): en ese caso no bloqueamos,
 * el backend tiene la última palabra.
 */
export const readVideoDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(video.duration) ? Math.round(video.duration) : 0);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    video.src = url;
  });

/**
 * Igual que `readVideoDuration` pero para el RECORTADOR: acepta también una URL ya subida
 * (los videos viejos de S3 no traen duración guardada) y devuelve MILISEGUNDOS sin redondear.
 * Un recortador es sub-segundo: redondear a segundos movería el corte hasta medio segundo,
 * que es justo el margen que la entrenadora está intentando afinar.
 *
 * Devuelve 0 si el navegador no pudo leer los metadatos; quien llama degrada la UI (sin
 * barra) en vez de pintar un slider con max=0.
 */
export const readVideoDurationMs = (source) =>
  new Promise((resolve) => {
    // Un File necesita objectURL (y revocarlo); una URL de S3 se usa tal cual.
    const isFile = typeof source !== 'string';
    const url = isFile ? URL.createObjectURL(source) : source;
    const video = document.createElement('video');
    let settled = false;
    let timer = null;

    const finish = (ms) => {
      // `onerror` puede llegar DESPUÉS del timeout (o al revés): sin este cerrojo se revocaría
      // dos veces el objectURL y la promesa quedaría con dos resoluciones distintas.
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (isFile) URL.revokeObjectURL(url);
      resolve(ms);
    };

    // Sin `crossOrigin`: la duración se lee sin CORS y pedirlo haría FALLAR la carga cuando
    // el bucket no manda las cabeceras (el video se vería, pero aquí no mediríamos nada).
    video.preload = 'metadata';
    video.muted = true;

    video.onloadedmetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        finish(video.duration * 1000);
        return;
      }
      // Los .webm que produce MediaRecorder (el grabador de la app) salen SIN duración en la
      // cabecera y el navegador reporta Infinity. Saltar al final obliga a calcularla y la
      // publica en el siguiente `timeupdate`.
      video.ontimeupdate = () => {
        video.ontimeupdate = null;
        finish(Number.isFinite(video.duration) && video.duration > 0 ? video.duration * 1000 : 0);
      };
      video.currentTime = 1e101;
    };

    video.onerror = () => finish(0);
    // Códec que no dispara ningún evento o red caída: la promesa NO puede quedar colgada,
    // el editor se quedaría con el skeleton de "midiendo" para siempre.
    timer = setTimeout(() => finish(0), 15000);

    video.src = url;
  });

/**
 * Valida tamaño y duración. Devuelve el MENSAJE de error listo para el toast, o null si
 * el archivo puede subirse.
 */
export const validateVideoFile = async (file, t) => {
  if (file.size > MAX_VIDEO_BYTES) {
    return t('recoveryAdmin.videoTooBig', {
      ns: 'play',
      mb: MAX_VIDEO_MB,
      defaultValue: 'El archivo pesa más de {{mb}} MB. Graba un clip más corto o comprímelo.'
    });
  }

  // Las imágenes de paso pasan por el mismo input: solo los videos tienen duración.
  if (!file.type.startsWith('video/')) return null;

  const seconds = await readVideoDuration(file);
  if (seconds > MAX_VIDEO_SECONDS) {
    return t('recoveryAdmin.videoTooLong', {
      ns: 'play',
      minutes: Math.round(MAX_VIDEO_SECONDS / 60),
      defaultValue: 'El video dura más de {{minutes}} minutos. Recórtalo antes de subirlo.'
    });
  }

  return null;
};
